import { addCommas, getCurrenciesValues, getUserCountryCached, resolveCurrencyByCountry, readCurrentCurrencyCookieValue } from "./commons/commonPricing";
import { readCookie, setCookie } from "./commons/cookies";
import { triggerChange } from "./commons/eventTarget";
import { applySpecialOfferDiscount, trimDiscount } from "./pricing/pricingDiscount";
import { currencyReducer } from "./pricing/pricingReducer";
import { PricingStore } from "./pricing/pricingStore";


const setCookies = (currencyName, currentCurrency) => {
  setCookie('currencySelector', currencyName);
  setCookie('currentCurrency', JSON.stringify(currentCurrency));
}

const initialState = {
  currenciesData: null,
  currentCurrency: null,
};

const FALLBACK_CURRENCY = 'USD';
let userSelectionMade = false;

const isRenderableCurrency = (code) => {
  const { currenciesData } = pricingStore.getState();
  return typeof code === 'string' && Boolean(currenciesData)
    && Object.prototype.hasOwnProperty.call(currenciesData, code);
}

const pricingStore = new PricingStore(currencyReducer, initialState);

const setCurrencies = (currencies) => ({
  type: 'SET_CURRENCIES',
  payload: currencies,
});

const setCurrentCurrency = (currencyCode) => ({
  type: 'SET_CURRENT_CURRENCY',
  payload: currencyCode,
});

const fetchCurrenciesData = async () => {
  const currenciesValues = await getCurrenciesValues();
  pricingStore.dispatch(setCurrencies(currenciesValues));
};

const decimalMapping = {
  'decimals-0': 0,
  'decimals-1': 0,
  'decimals-2': 2,
  'decimals-3': 3,
  'decimals-4': 4,
  'decimals-5': 5,
};


const convertFromDollar = (amountInUSD, targetCurrencyCode, roundingDecimals = 'decimals-2') => {
  const state = pricingStore.getState();
  const currenciesData = state.currenciesData;

  if (!currenciesData || !currenciesData[targetCurrencyCode]) {
    throw new Error(`Moneda no encontrada: ${targetCurrencyCode}`);
  }

  const currency = currenciesData[targetCurrencyCode];
  const rate = parseFloat(currency.rateValue);
  const convertedAmount = amountInUSD * rate;


  const decimalPlaces = decimalMapping[roundingDecimals];
  const formattedAmount = convertedAmount.toFixed(decimalPlaces);


  return {
    amount: formattedAmount,
    currencySymbol: currency.currencySymbol,
    currencyName: currency.currencyName
  };
};

const updateSelectorValue = () => {
  const selector = document.querySelector('.currencySelector');

  const state = pricingStore.getState();
  const currentCurrency = state.currentCurrency;

  selector.value = currentCurrency;
}

const getConvertedValue = (value, currency, roundingDecimals) => {
  const { amount } = convertFromDollar(value, currency, roundingDecimals);
  return addCommas(amount);
};

const getCurrencySymbol = (symbol) => {
  return symbol === 'euro' ? '€' : symbol;
};


const updateDOMValue = (element, newValue) => {
  if (element.innerHTML !== newValue) {
    element.innerHTML = newValue;
    restartAnimation(element);
  }
};

const updateCurrencyValues = () => {
  const { currentCurrency, currenciesData } = pricingStore.getState();
  const currentCurrencySymbol = currenciesData[currentCurrency].currencySymbol;
  const parentsCurrency = document.querySelectorAll('.parentCurrency');

  parentsCurrency.forEach(parent => {
    const currencyValue = parent.querySelector('.currencyValueToChange');
    const currencySymbol = parent.querySelector('.currencySymbolToChange');

    const value = currencyValue.value;
    // roundingDecimals: It is a string that represents the number of decimal places
    // with which the new value corresponding to newValue will be returned
    const roundingDecimals = currencyValue.getAttribute('roundingDecimals');

    const newValue = getConvertedValue(value, currentCurrency, roundingDecimals);
    const newCurrencySymbol = getCurrencySymbol(currentCurrencySymbol);

    updateDOMValue(currencyValue, newValue);
    updateDOMValue(currencySymbol, newCurrencySymbol);

  });

}

const restartAnimation = (htmlElement) => {
  htmlElement.style.animation = 'none';
  requestAnimationFrame(() => {
    htmlElement.style.animation = '';
  });
}

const changeSmsSelectorCurrency = (currencyCode) => {
  //This function is used to emulate the selector change in the sms tab,
  //emulating the selector change and its event
  const smsSelector = document.getElementById('curr-code-sms');
  if (!smsSelector) return;
  smsSelector.value = currencyCode;
  triggerChange(smsSelector);
}

const changeSelectorCurrency = (selector) => {
  const currencyCode = selector.value;
  const state = pricingStore.getState();
  const currenciesData = state.currenciesData;

  userSelectionMade = true;
  setCookies(currencyCode, currenciesData[currencyCode]);
  pricingStore.dispatch(setCurrentCurrency(currencyCode));
  changeSmsSelectorCurrency(currencyCode);
}

const activeSelectorListener = () => {
  const selector = document.querySelector('.currencySelector');
  selector.addEventListener('change', () => changeSelectorCurrency(selector));
}


const initializeUserCurrency = async () => {
  // La misma precedencia que aplica el módulo legacy, para que ambos consumidores terminen en una sola moneda.
  const cookieCurrency = [readCookie('currencySelector'), readCurrentCurrencyCookieValue()]
    .find(isRenderableCurrency);
  if (cookieCurrency !== undefined) {
    pricingStore.dispatch(setCurrentCurrency(cookieCurrency));
    return;
  }

  let currency = FALLBACK_CURRENCY;
  try {
    const resolved = resolveCurrencyByCountry(await getUserCountryCached());
    if (isRenderableCurrency(resolved)) {
      currency = resolved;
    }
  } catch (error) {
    // La geolocalización es de mejor esfuerzo: se conserva el respaldo en USD en lugar de bloquear la página.
    console.error('No se pudo resolver la moneda por país:', error);
  }

  // El visitante eligió una moneda mientras la geolocalización estaba en curso; su elección gana.
  if (userSelectionMade) return;

  pricingStore.dispatch(setCurrentCurrency(currency));
}


const activeStoreSubscription = () => {
  pricingStore.subscribe(() => {
    updateCurrencyValues();
    updateSelectorValue();
    trimDiscount();
    applySpecialOfferDiscount(pricingStore);
  });
}


(async () => {
  const isPricingSelector = document.querySelector('.currencySelector');
  if (!isPricingSelector) return;

  // El select es interactivo desde el primer renderizado, antes de que exista el listener. Una moneda
  // elegida mientras cargaban las cotizaciones quedaría sobrescrita más abajo.
  const selectorValueOnLoad = isPricingSelector.value;
  await fetchCurrenciesData();
  activeStoreSubscription();
  activeSelectorListener();
  if (isPricingSelector.value !== selectorValueOnLoad && isRenderableCurrency(isPricingSelector.value)) {
    changeSelectorCurrency(isPricingSelector);
  }
  await initializeUserCurrency();
  applySpecialOfferDiscount(pricingStore);
})();


export {getConvertedValue}
